// The card generator: a resolved job's route steps → travelers for the shop.
//
// A card follows a cart through the steps one person runs in one go and ends where the
// work changes hands. This first version breaks wherever the phase changes (falling back
// to the operation's category while an operation has no phase), then merges a card that
// is under the floor into its same-phase neighbour as long as the pair stays under the cap.
// Cap and floor are settings, never constants here.

import { Rat } from './rational.ts';
import type { Library, Resolution, ResolvedBom, ResolvedStep } from './types.ts';

export interface CardStep {
  operation_id: string;
  operation_name: string;
  work_center_id: string | null;
  qty: string;
  unit: string | null;
  minutes: string;
}

export interface GeneratedCard {
  name: string;
  phase_id: string | null;
  /** The label the break was made on: the phase name, or the category while there is no phase. */
  break_label: string;
  item_ids: string[];
  steps: CardStep[];
  bom: { material_id: string | null; description: string; qty: string; unit: string | null }[];
  minutes: string;
  hours: string;
}

export interface CardOptions {
  capHours: number;
  floorHours: number;
}

interface Draft {
  label: string;
  phase_id: string | null;
  groups: string[];
  item_ids: Set<string>;
  steps: ResolvedStep[];
  bom: ResolvedBom[];
  minutes: Rat;
}

export function generateCards(res: Resolution, lib: Library, opts: CardOptions): GeneratedCard[] {
  const cap = Rat.parse(opts.capHours).mul(Rat.of(60n));
  const floor = Rat.parse(opts.floorHours).mul(Rat.of(60n));
  const topLevel = new Map(res.items.map((i) => [i.id, i]));

  const drafts: Draft[] = [];
  for (const g of res.groups) {
    let current: Draft | null = null;
    let first = true;
    for (const s of g.steps) {
      const minutes = Rat.parse(s.minutes);
      if (minutes.sign() <= 0) continue;
      const label = (s.phase_id && lib.phases[s.phase_id]?.name) || s.category || 'General';
      if (!current || current.label !== label) {
        current = { label, phase_id: s.phase_id, groups: [g.name], item_ids: new Set(g.item_ids), steps: [], bom: [], minutes: Rat.ZERO };
        // the group's materials ride on its first card: that is where they leave the rack
        if (first) current.bom = g.bom.filter((b) => Rat.parse(b.qty).sign() > 0);
        first = false;
        drafts.push(current);
      }
      current.steps.push(s);
      current.minutes = current.minutes.add(minutes);
    }
  }

  // merge an under-floor card into the previous card with the same label while it fits
  const merged: Draft[] = [];
  for (const d of drafts) {
    const prev = [...merged].reverse().find((m) => m.label === d.label);
    if (prev && (prev.minutes.cmp(floor) < 0 || d.minutes.cmp(floor) < 0) && prev.minutes.add(d.minutes).cmp(cap) <= 0) {
      prev.steps.push(...d.steps);
      prev.bom.push(...d.bom);
      d.groups.forEach((n) => prev.groups.includes(n) || prev.groups.push(n));
      d.item_ids.forEach((id) => prev.item_ids.add(id));
      prev.minutes = prev.minutes.add(d.minutes);
    } else {
      merged.push(d);
    }
  }

  return merged.map((d) => ({
    name: `${d.label} · ${d.groups.join(' + ')}`,
    phase_id: d.phase_id,
    break_label: d.label,
    item_ids: [...d.item_ids].filter((id) => topLevel.has(id)),
    steps: d.steps.map((s) => ({
      operation_id: s.operation_id,
      operation_name: s.operation_name,
      work_center_id: s.work_center_id,
      qty: s.qty,
      unit: s.unit,
      minutes: s.minutes,
    })),
    bom: d.bom.map((b) => ({ material_id: b.material_id, description: b.description, qty: b.qty, unit: b.unit })),
    minutes: d.minutes.toDecimal(2),
    hours: d.minutes.div(Rat.of(60n)).toDecimal(2),
  }));
}
